'use client'

import { useSyncExternalStore } from 'react'

/** Ścieżki w `public/zebra-logos/SVG TM/` */
const TM = '/zebra-logos/SVG TM'

/** Jasny navbar / jasne tło: pomarańczowy kwadrat, biały marker, ciemny napis „zebra”. */
export const ZEBRA_LOGO_LIGHT_SRC = `${TM}/zebra-color.svg`

/** Ciemny navbar: biała ramka (kwadrat), ciemny marker, biały napis — plik `zebra-white.svg`. */
export const ZEBRA_LOGO_DARK_SRC = `${TM}/zebra-white.svg`

/** Wariant pomarańczowy: kwadrat main, marker i napis białe (`zebra-color-white.svg`). */
export const ZEBRA_LOGO_ORANGE_SRC = `${TM}/zebra-color-white.svg`

/** Sygnet (tylko ikona) na jasne tło: pomarańczowy kwadrat, biały marker (marketing itd.). */
export const ZEBRA_SYGNET_LIGHT_SRC = `${TM}/zebra-sygnet-color.svg`

/** Sygnet na ciemne tło: biała ramka, ciemny marker (`zebra-sygnet-on-dark.svg`). */
export const ZEBRA_SYGNET_DARK_SRC = `${TM}/zebra-sygnet-on-dark.svg`

/** Dashboard sidebar — light: czarno-białe (`zebra-black.svg`), nie pomarańczowe. */
export const ZEBRA_LOGO_DASHBOARD_LIGHT_SRC = `${TM}/zebra-black.svg`

/** Dashboard sidebar zwinięty — light: sygnet szary + biały znak (`zebra-sygnet.svg`). */
export const ZEBRA_SYGNET_DASHBOARD_LIGHT_SRC = `${TM}/zebra-sygnet.svg`

function subscribe(onStoreChange: () => void) {
  const observer = new MutationObserver(onStoreChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  })
  const onZebraTheme = () => onStoreChange()
  window.addEventListener('zebra-theme', onZebraTheme)
  window.addEventListener('storage', onZebraTheme)
  return () => {
    observer.disconnect()
    window.removeEventListener('zebra-theme', onZebraTheme)
    window.removeEventListener('storage', onZebraTheme)
  }
}

function getThemeSnapshot(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light'
}

function getServerThemeSnapshot(): 'light' | 'dark' {
  return 'light'
}

export function useDataTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    subscribe,
    getThemeSnapshot,
    getServerThemeSnapshot
  )
}

/** Logo np. maintenance: jasny = kolor klasyczny, ciemny = biała ramka (`zebra-white.svg`). Stopka używa `useNavbarLogoSrc`. */
export function useZebraLogoSrc(): string {
  const theme = useDataTheme()
  return theme === 'dark' ? ZEBRA_LOGO_DARK_SRC : ZEBRA_LOGO_LIGHT_SRC
}

/**
 * Logo w **navbarze** i **stopce** (marketing): w dark mode pomarańczowy kwadrat, biały znak i napis —
 * `zebra-color-white.svg` (nie `zebra-white.svg`).
 */
export function useNavbarLogoSrc(): string {
  const theme = useDataTheme()
  return theme === 'dark' ? ZEBRA_LOGO_ORANGE_SRC : ZEBRA_LOGO_LIGHT_SRC
}

/** Sam sygnet (zwinięty sidebar) — te same warianty co pełne logo, bez napisu „zebra”. */
export function useZebraSygnetSrc(): string {
  const theme = useDataTheme()
  return theme === 'dark' ? ZEBRA_SYGNET_DARK_SRC : ZEBRA_SYGNET_LIGHT_SRC
}

/** Logo w **panelu** (sidebar + mobile header): light = czarno-białe, dark = jasne na ciemnym (`zebra-white.svg`). */
export function useDashboardLogoSrc(): string {
  const theme = useDataTheme()
  return theme === 'dark' ? ZEBRA_LOGO_DARK_SRC : ZEBRA_LOGO_DASHBOARD_LIGHT_SRC
}

/** Sygnet w panelu przy zwiniętym sidebarze — spójnie z `useDashboardLogoSrc`. */
export function useDashboardSygnetSrc(): string {
  const theme = useDataTheme()
  return theme === 'dark' ? ZEBRA_SYGNET_DARK_SRC : ZEBRA_SYGNET_DASHBOARD_LIGHT_SRC
}
